import React, { FC, useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import debounce from 'debounce-promise';
import memoize from 'memoizee';

import {
  MidotUserDTO,
  useAutocompleteUsersHook,
  useGetMidotUsersHook,
  useGetUsersByIdsHook,
  User,
  UserSummaryDTO
} from '@/api/generated';
import DropdownSelect, { DropdownSelectOption, DropdownSelectProps } from '@/components/fields/DropdownSelect';
import { UserAvatarForDropdown } from '@/components/UserAvatar';
import { getUserFullName } from '@/utils/user';

const DEBOUNCE_DELAY = 300;

type CreateByDropdownSelectProps = Omit<DropdownSelectProps, 'options'> & {
  organizationSlug?: string;
  officeSlug?: string[];
  roleId?: string;
};

const filterBySearch = (options: DropdownSelectOption[], inputValue?: string) => {
  if (!inputValue) return options;

  return options.filter(option => (option.label || '').toLowerCase().includes(inputValue?.toLowerCase() || ''));
};

const userToOption = (user: User) => ({
  label: getUserFullName(user),
  value: user.id!,
  labelElement: (
    <Stack gap={0.5}>
      <Typography variant="body2">{getUserFullName(user) || user.email}</Typography>
      <Typography variant="body2" color="text.secondary">
        {user.email}
      </Typography>
    </Stack>
  ),
  Icon: <UserAvatarForDropdown user={user} sx={{ ml: 0.5, mr: 0.5 }} />
});

const CreateByDropdownSelect: FC<CreateByDropdownSelectProps> = props => {
  const getClientUsers = useAutocompleteUsersHook();
  const getMidotUsers = useGetMidotUsersHook();
  const getUsersByIds = useGetUsersByIdsHook();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptions = useCallback(
    memoize(
      async (inputValue?: string) => {
        const clientUsersData = await getClientUsers({ q: inputValue, organizationId: props.organizationSlug });

        const midotUsersData = await getMidotUsers({
          query: inputValue,
          role_id: props.roleId
        });

        const clientUserOptions = (clientUsersData?.userSummaries || []).map((userSummary: UserSummaryDTO) =>
          userToOption(userSummary as User)
        );

        const midotUserOptions = (midotUsersData?.users || []).map((midotUser: MidotUserDTO) =>
          userToOption(midotUser as User)
        );

        const allUserOptions = [...clientUserOptions, ...midotUserOptions];

        return filterBySearch(allUserOptions, inputValue);
      },
      { promise: true }
    ),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsDebounced = useCallback(debounce(loadUserOptions, DEBOUNCE_DELAY), [loadUserOptions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsByIds = useCallback(
    memoize(
      async (ids?: string[]) => {
        const users = await getUsersByIds({ id: ids! });
        const clientUserOptions = (users || []).map((user: User) => userToOption(user));

        return clientUserOptions;
      },
      { promise: true }
    ),
    []
  );

  return (
    <DropdownSelect {...props} loadOptions={loadUserOptionsDebounced} loadSelectedOptions={loadUserOptionsByIds} />
  );
};

export default CreateByDropdownSelect;
